'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useInsufficientCreditsToast } from '@/new_components/alerts/use-insufficient-credits-toast';
import { Bookmark, BookmarkCheck, FileText, ScrollText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { trackSignupAndRedirect } from '@/lib/track-signup-click';
import { cn } from '@/lib/utils';
import { useWatchlist } from '@/hooks/use-watchlist';
import {
  type CompanyAlertDialog,
  CompanyAlertDialogs,
} from './CompanyAlertDialogs';

/**
 * Bottom-fixed action bar shown only below `lg`.
 *
 * The mobile stack (see `MOBILE_ORDER` in CompanyDetailPage) puts the two
 * monetised cards near the top, but a user who has scrolled into the history or
 * the charts has no way back to them short of scrolling. This bar keeps them one
 * tap away at any depth.
 *
 * **The document buttons scroll to their card; they never start a download.**
 * Both actions cost credits (`WEB_CREDIT_COSTS`), and the rule that config
 * documents — a user must never learn a price by paying it — means the purchase
 * has to happen on a control that shows its price. Scrolling to the card puts
 * the user in front of exactly that control.
 *
 * A button is rendered only when its target card exists, so a company with no
 * Bilanz on file gets a two-button bar rather than one that scrolls nowhere.
 * The bar stays hidden until the user is past the first viewport, so it never
 * covers content in the initial frame (and never appears on a page short enough
 * not to scroll).
 */
export function MobileActionBar({
  euId,
  isAuthenticated,
  financialDocsAnchor,
  registerDocsAnchor,
  hasFinancialDocs,
  hasRegisterDocs,
}: {
  euId: string;
  isAuthenticated: boolean;
  /** Element id of the Jahresabschlüsse card. */
  financialDocsAnchor: string;
  /** Element id of the Registerdateien card. */
  registerDocsAnchor: string;
  hasFinancialDocs: boolean;
  hasRegisterDocs: boolean;
}) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const { isWatched, toggle, isMutating } = useWatchlist(euId);
  const showInsufficient = useInsufficientCreditsToast();
  const [visible, setVisible] = useState(false);
  const [alertDialog, setAlertDialog] = useState<CompanyAlertDialog>(null);

  // Plain listener with a crossing check rather than `useScrollPosition`, which
  // stores the offset in state and would re-render this subtree — and with it
  // the toast/dialog portals — on every scroll tick.
  useEffect(() => {
    const onScroll = () => {
      const past = window.scrollY > window.innerHeight * 0.6;
      setVisible((prev) => (prev === past ? prev : past));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /**
   * Scroll a card to just below the sticky header.
   *
   * `scrollIntoView({block: 'start'})` puts the card's top at viewport 0, which
   * is *behind* `.fd-header` (`sticky top-0`) — the card's own title ends up
   * hidden and the section looks like it starts mid-content.
   *
   * The offset is measured rather than taken from `--header-height`: that
   * variable describes the desktop row (64px, or 52px once the header
   * collapses), while the mobile header is HeaderMobile's two-row `flex-col`
   * and is a different height again. Reading the rendered box covers all three
   * states, and any future change to them.
   */
  const scrollTo = (anchor: string) => {
    const el = document.getElementById(anchor);
    if (!el) return;
    const header = document.querySelector('.fd-header');
    const headerHeight = header ? header.getBoundingClientRect().height : 0;
    const top =
      el.getBoundingClientRect().top + window.scrollY - headerHeight - 8;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  };

  const handleWatchlist = async () => {
    if (!isAuthenticated) {
      trackSignupAndRedirect(pathname, 'mobile_bar_watchlist', euId);
      return;
    }
    const action = await toggle();
    if (action === 'added') {
      // Saving is free; the prompt offers the (priced) updates subscription.
      setAlertDialog('subscribe');
    } else if (action === 'removed') {
      toast.success(t('watchlist.removed', 'Removed from Watchlist'), {
        position: 'top-center',
      });
    } else if (action === 'credits') {
      showInsufficient();
    }
  };

  if (!hasFinancialDocs && !hasRegisterDocs) return null;

  const itemClass =
    'flex flex-1 flex-col items-center justify-center gap-0.5 min-h-[52px] px-2 text-[11px] font-medium text-foreground transition-colors hover:bg-muted/60 active:bg-muted disabled:opacity-50';

  return (
    <>
      <div
        className={cn(
          'lg:hidden fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur-sm transition-transform duration-200',
          // The safe-area padding keeps the row clear of the iOS home
          // indicator. Careful with class-shaped text in comments here: the
          // Tailwind scanner reads this file as plain text, so an abbreviated
          // utility written in prose becomes a real (and possibly invalid)
          // rule in the stylesheet.
          'pb-[env(safe-area-inset-bottom)]',
          visible ? 'translate-y-0' : 'translate-y-full',
        )}
        // Purely supplementary navigation — hidden from assistive tech while
        // off-screen so it doesn't sit in the tab order behind the page.
        aria-hidden={!visible}
        inert={!visible}
      >
        <div className="flex items-stretch divide-x divide-border">
          {hasFinancialDocs && (
            <button
              type="button"
              className={itemClass}
              onClick={() => scrollTo(financialDocsAnchor)}
            >
              <FileText className="size-4 shrink-0" aria-hidden />
              {t('companyDetail.mobileBar.bilanz', 'Bilanz')}
            </button>
          )}
          {hasRegisterDocs && (
            <button
              type="button"
              className={itemClass}
              onClick={() => scrollTo(registerDocsAnchor)}
            >
              <ScrollText className="size-4 shrink-0" aria-hidden />
              {t('companyDetail.mobileBar.register', 'Registerauszug')}
            </button>
          )}
          <button
            type="button"
            className={itemClass}
            onClick={handleWatchlist}
            disabled={isAuthenticated && isMutating}
            aria-pressed={isAuthenticated ? isWatched : undefined}
          >
            {isAuthenticated && isWatched ? (
              <BookmarkCheck
                className="size-4 shrink-0 fill-current"
                aria-hidden
              />
            ) : (
              <Bookmark className="size-4 shrink-0" aria-hidden />
            )}
            {t('companyDetail.mobileBar.watchlist', 'Merken')}
          </button>
        </div>
      </div>
      {isAuthenticated && (
        <CompanyAlertDialogs
          euId={euId}
          open={alertDialog}
          onOpenChange={setAlertDialog}
        />
      )}
    </>
  );
}
